/* @flow */
import produce from 'immer';
import { message } from 'antd';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import { actionTypes } from './actions';
import { actionTypes as leadActionsTypes } from '../lead/actions';
import { defineServerErrors } from '@/utils/redux';

const {
  FETCH_PROPOSALS,
  FETCH_SUCCESS,
  FETCH_FAILED,

  FETCH_PROPOSAL,
  FETCH_PROPOSAL_SUCCESS,
  FETCH_PROPOSAL_FAILED,

  CREATE_PROPOSAL,
  CREATE_PROPOSAL_SUCCESS,
  CREATE_PROPOSAL_FAILED,

  TABLE_CHANGE,

  EDIT_PROPOSAL,
  EDIT_PROPOSAL_SUCCESS,
  EDIT_PROPOSAL_FAILED,

  GENERATE_PROPOSAL,
  GENERATE_PROPOSAL_SUCCESS,
  GENERATE_PROPOSAL_FAILED,

  SET_LOADING_PERCENT,
  ADD_CREATE_ERROR,
  REMOVE_CREATE_ERROR,
  CLEAR_CREATE_ERRORS,
  UPLOAD_CUSTOM_TEMPLATE,
  SHOW_MARKETING_PREVALIDATE_MESSAGES,
  REMOVE_MARKETING_PREVALIDATE_MESSAGE,
  CLEAR_MARKETING_PREVALIDATE_MESSAGES,
} = actionTypes;

type State = {
  isFetching: boolean,
  tableOptions: TableT,
  data: ProposalT[],
  fetchErrors: Array<string>,
  isCreatingProposal: boolean,
  createErrors: Array<Object>,
  isEditingProposal: boolean,
  editErrors: Array<string>,
  isGeneratingProposal: boolean,
  generateErrors: Array<string>,
  editFormData: Object,
  percent: number,
  proposalData: ProposalT,
  marketingPrevalidateData: Object,
};

const initialState = {
  isFetching: false,
  tableOptions: {
    page: 1,
    limit: 10,
    count: 0,
  },
  data: [],
  fetchErrors: [],
  isCreatingProposal: false,
  createErrors: [],
  isEditingProposal: false,
  editErrors: [],
  isGeneratingProposal: false,
  generateErrors: [],
  editFormData: {},
  percent: 0,
  proposalData: {},
  marketingPrevalidateData: null,
};

export default (state: State = initialState, action: ActionT = {}): State =>
  produce(state, (draft) => {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case FETCH_PROPOSALS:
        draft.isFetching = true;
        return;
      case FETCH_SUCCESS: {
        const { results, page, limit, count } = action.payload;
        draft.isFetching = false;
        draft.data = results;
        draft.fetchErrors = initialState.fetchErrors;
        draft.tableOptions = {
          page,
          limit,
          count,
        };
        return;
      }
      case FETCH_FAILED: {
        draft.isFetching = false;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        return;
      }
      case FETCH_PROPOSAL:
        draft.isFetching = true;
        return;
      case FETCH_PROPOSAL_SUCCESS: {
        draft.isFetching = false;
        draft.proposalData = action.payload;
        draft.fetchErrors = initialState.fetchErrors;
        return;
      }
      case FETCH_PROPOSAL_FAILED: {
        draft.isFetching = false;
        draft.fetchErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.fetchErrors.forEach((error) => message.error(error, 10));
        return;
      }
      case CREATE_PROPOSAL:
        draft.isCreatingProposal = true;
        draft.createErrors = initialState.createErrors;
        return;
      case CREATE_PROPOSAL_SUCCESS:
        draft.isCreatingProposal = false;
        draft.proposalData = action.payload;
        draft.createErrors = initialState.createErrors;
        action.payload.proposal_status === 0
          ? action.payload.proposal_type === 0
            ? message.success(`Quote requested successfully.`)
            : message.success(`Proposal #${action.payload.id} requested successfully.`)
          : message.success(`Proposal #${action.payload.id} generated successfully.`);
        return;
      case CREATE_PROPOSAL_FAILED:
        draft.isCreatingProposal = false;
        draft.createErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        return;
      case EDIT_PROPOSAL:
        draft.isEditingProposal = true;
        draft.editErrors = initialState.editErrors;
        message.info('Proposal is saving. Please wait.');
        return;
      case EDIT_PROPOSAL_SUCCESS:
        draft.isEditingProposal = false;
        draft.proposalData = action.payload;
        draft.editErrors = initialState.editErrors;
        draft.editFormData = initialState.editFormData;
        message.success(`Proposal #${action.payload.id} updated successfully.`);
        return;
      case EDIT_PROPOSAL_FAILED:
        draft.isEditingProposal = false;
        draft.editErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.editErrors.forEach((error) => message.error(error, 10));
        return;
      case GENERATE_PROPOSAL:
        draft.isGeneratingProposal = true;
        draft.generateErrors = initialState.generateErrors;
        message.info('Proposal is being generated. Please wait.', 5);
        return;
      case GENERATE_PROPOSAL_SUCCESS:
        draft.isGeneratingProposal = false;
        draft.generateErrors = initialState.generateErrors;
        message.success(`Proposal #${action.payload.id} generated successfully.`);
        return;
      case GENERATE_PROPOSAL_FAILED:
        draft.isGeneratingProposal = false;
        draft.generateErrors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.generateErrors.forEach((error) => message.error(error, 10));
        return;
      case TABLE_CHANGE:
        draft.tableOptions = action.payload;
        return;
      case SET_LOADING_PERCENT:
        draft.percent = action.payload;
        return;
      case ADD_CREATE_ERROR: {
        draft.createErrors.push(action.payload);
        draft.createErrors = uniqWith(draft.createErrors, isEqual);
        return;
      }
      case REMOVE_CREATE_ERROR: {
        const index = draft.createErrors.findIndex((error) => {
          if (!error.key) {
            return error === action.payload;
          }
          return error.key === action.payload.key;
        });
        if (index !== -1) {
          draft.createErrors.splice(index, 1);
        }
        return;
      }
      case CLEAR_CREATE_ERRORS:
      case leadActionsTypes.FETCH_LEAD:
        draft.createErrors = initialState.createErrors;
        return;
      case UPLOAD_CUSTOM_TEMPLATE:
        draft.isEditingProposal = true;
        return;
      case SHOW_MARKETING_PREVALIDATE_MESSAGES:
        draft.marketingPrevalidateData = action.payload;
        return;
      case REMOVE_MARKETING_PREVALIDATE_MESSAGE: {
        const index = draft.marketingPrevalidateData.messages.findIndex((error) => error.key === action.payload.key);
        if (index !== -1) draft.marketingPrevalidateData.messages.splice(index, 1);
        return;
      }
      case CLEAR_MARKETING_PREVALIDATE_MESSAGES:
        draft.marketingPrevalidateData = initialState.marketingPrevalidateData;
        return;
    }
  });
